import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Markdown, Text } from '@Sage/hoi-poi-ui';

import Map from 'components/Map';
import MapCircle from 'components/Map/MapCircle';
import MapMarkerWithTooltip from 'components/Map/MapMarkerWithTooltip';
import { COMPANY_ICON, COMPANY_ICON_GREY, MAP_PIN } from 'constants/Images';
import { getLiteral } from 'utils/getLiteral';

import LeadsMapOriginControl from './LeadsMapOriginControl';

const RADIUS_ANIM_MS = 380;
const POSITION_EPSILON = 1e-5;

const easeOutCubic = (t) => 1 - (1 - t) ** 3;

const isSamePosition = (left, right) => {
    if (!left || !right) return false;
    const latA = Number(left.lat);
    const lngA = Number(left.lng);
    const latB = Number(right.lat);
    const lngB = Number(right.lng);
    if (
        !Number.isFinite(latA) ||
        !Number.isFinite(lngA) ||
        !Number.isFinite(latB) ||
        !Number.isFinite(lngB)
    ) {
        return false;
    }
    return Math.abs(latA - latB) < POSITION_EPSILON && Math.abs(lngA - lngB) < POSITION_EPSILON;
};

/**
 * Google Maps Circle API requires hex/rgb (not CSS variables).
 * Values mirror theme `actionMajor-500` / `$jade-accessible` in `sass/styleguide.scss`.
 */
const LEADS_MAP_RADIUS_CIRCLE = {
    fillColor: '#007e45',
    fillOpacity: 0.18,
    strokeColor: '#007e45',
    strokeOpacity: 0.8,
    strokeWeight: 1,
};

const LEADS_MAP_OPTIONS = {
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    rotateControl: false,
    scaleControl: false,
};

const MAP_TOOLTIP_TEXT_COLOR = 'var(--primary-white)';

/** Tooltip: name, then business type (category) on the next line — no separate field label. */
const buildLeadTooltipContent = (lead) => (
    <div className="fm-leads-modal__map-tooltip">
        <Text as="div" type="caption" bold color={MAP_TOOLTIP_TEXT_COLOR}>
            {lead.name}
        </Text>
        {lead.category ? (
            <Text
                as="div"
                type="caption"
                color={MAP_TOOLTIP_TEXT_COLOR}
                className="fm-leads-modal__map-tooltip-type"
            >
                {lead.category}
            </Text>
        ) : null}
    </div>
);

const isLeadSelected = (selectedIds, leadId) =>
    selectedIds.some((sid) => String(sid) === String(leadId));

/** Markers match the list: same bucket as `LeadList` (`activeLeads`). */
const LeadsMap = ({
    activeLeads = [],
    selectedIds = [],
    origin,
    initialOrigin = null,
    userPosition = null,
    radiusKm,
    onOriginChange,
    onToggleLead,
    onMapReadyChange,
}) => {
    const mapRef = useRef(null);
    const targetRadiusMeters = Number(radiusKm) * 1000;
    const [animatedRadiusMeters, setAnimatedRadiusMeters] = useState(targetRadiusMeters);
    const animatedRadiusRef = useRef(targetRadiusMeters);
    const rafRef = useRef(null);

    const resetTarget = initialOrigin;
    const isAwayFromStart = Boolean(resetTarget && !isSamePosition(resetTarget, origin));
    const resetToMyLocationLabel = getLiteral('action_leads_reset_to_my_location');

    /** GPS pin when available; otherwise pin at session start when the search origin moved. */
    const referencePinPosition = useMemo(() => {
        if (userPosition && !isSamePosition(userPosition, origin)) {
            return userPosition;
        }
        if (resetTarget && isAwayFromStart && !userPosition) {
            return resetTarget;
        }
        return null;
    }, [isAwayFromStart, origin, resetTarget, userPosition]);

    const handleRecenterToStart = useCallback(() => {
        if (!resetTarget) return;
        onOriginChange(resetTarget);
        mapRef.current?.setCenter?.(resetTarget);
    }, [onOriginChange, resetTarget]);

    const handleMapLoad = useCallback(() => {
        onMapReadyChange?.(true);
    }, [onMapReadyChange]);

    const handleMapUnmount = useCallback(() => {
        onMapReadyChange?.(false);
    }, [onMapReadyChange]);

    useEffect(() => {
        const start = animatedRadiusRef.current;
        if (Math.abs(start - targetRadiusMeters) < 0.5) {
            animatedRadiusRef.current = targetRadiusMeters;
            setAnimatedRadiusMeters(targetRadiusMeters);
            return undefined;
        }

        if (rafRef.current != null) cancelAnimationFrame(rafRef.current);

        const t0 = performance.now();

        const tick = (now) => {
            const elapsed = now - t0;
            const t = Math.min(1, elapsed / RADIUS_ANIM_MS);
            const next = start + (targetRadiusMeters - start) * easeOutCubic(t);
            animatedRadiusRef.current = next;
            setAnimatedRadiusMeters(next);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                animatedRadiusRef.current = targetRadiusMeters;
                setAnimatedRadiusMeters(targetRadiusMeters);
                rafRef.current = null;
            }
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [targetRadiusMeters]);

    const circleOptions = useMemo(
        () => ({
            center: origin,
            radius: animatedRadiusMeters,
            options: LEADS_MAP_RADIUS_CIRCLE,
        }),
        [animatedRadiusMeters, origin],
    );

    return (
        <div className="fm-leads-modal__map">
            <Map
                ref={mapRef}
                center={origin}
                zoom={13}
                options={LEADS_MAP_OPTIONS}
                onLoadMap={handleMapLoad}
                onUnmount={handleMapUnmount}
            >
                <LeadsMapOriginControl
                    resetTarget={resetTarget}
                    ariaLabel={resetToMyLocationLabel}
                    onRecenter={handleRecenterToStart}
                />
                <MapCircle {...circleOptions} />
                {referencePinPosition && (
                    <MapMarkerWithTooltip
                        position={referencePinPosition}
                        icon={{ url: MAP_PIN }}
                        title={resetToMyLocationLabel}
                    />
                )}
                <MapMarkerWithTooltip
                    position={origin}
                    draggable
                    title={getLiteral('label_leads_search_origin')}
                    onDragEnd={(event) => {
                        const lat = event?.latLng?.lat?.();
                        const lng = event?.latLng?.lng?.();
                        if (Number.isFinite(lat) && Number.isFinite(lng))
                            onOriginChange({ lat, lng });
                    }}
                />
                {activeLeads.map((lead) => (
                    <MapMarkerWithTooltip
                        key={lead.id}
                        position={lead.position}
                        icon={{
                            url: isLeadSelected(selectedIds, lead.id)
                                ? COMPANY_ICON
                                : COMPANY_ICON_GREY,
                        }}
                        tooltipContent={buildLeadTooltipContent(lead)}
                        onClick={() => onToggleLead(lead.id)}
                    />
                ))}
            </Map>
            <div className="fm-leads-modal__map-help">
                <Markdown content={getLiteral('label_leads_drag_pin_help')} />
            </div>
        </div>
    );
};

export default memo(LeadsMap);
